/**
 * Helpers para el calendario semanal por (usuario, comunidad).
 *
 * El backend representa el schedule con shape:
 *   { days: [[{start, end, sede?}, ...], ...] }   // array de 7 días
 *
 *   start, end: minutos desde medianoche (0..1440), múltiplos de 5
 *   sede:       opcional, debe matchear (case-sensitive) con un elemento
 *               de community.sedes
 *
 * Los días son: 0=Lun, 1=Mar, 2=Mié, 3=Jue, 4=Vie, 5=Sáb, 6=Dom.
 */

export const DAYS_PER_WEEK = 7;
export const MAX_RANGES_PER_DAY = 3;
export const MAX_MINUTES = 1440;
export const TIME_GRANULARITY = 5;
export const MAX_SEDES = 8;
export const MAX_SEDE_LENGTH = 32;

export const DAY_LABELS_LONG_ES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
export const DAY_LABELS_MEDIUM_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
export const DAY_LABELS_LONG_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const DAY_LABELS_MEDIUM_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** "8:30" → 510. Devuelve null si el input es inválido. */
export function hhmmToMinutes(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (h < 0 || h > 24) return null;
  if (mm < 0 || mm >= 60) return null;
  const total = h * 60 + mm;
  if (total > MAX_MINUTES) return null;
  return total;
}

/** 510 → "08:30". Pads a 2 dígitos para inputs HTML <input type="time">. */
export function minutesToHHMM(total) {
  if (total == null || total < 0) return '';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
}

/** Esquema vacío (7 días, ninguno con rangos). Util para inicializar editores. */
export function emptySchedule() {
  return { days: Array.from({ length: DAYS_PER_WEEK }, () => []) };
}

/**
 * Compacta + valida el shape del schedule:
 *  - exactamente 7 días,
 *  - rangos válidos (start<end, en [0, 1440], múltiplos de 5),
 *  - sede (si presente) debe estar en `validSedes`,
 *  - ordena cada día por start ascendente,
 *  - max 3 rangos por día.
 *
 * Devuelve { schedule, errors[] }. Si `errors` está vacío, schedule es OK
 * para mandar al server. El server hace su propia validación — esto es
 * principalmente para que el UI muestre errores antes del submit.
 */
export function validateSchedule(schedule, validSedes = []) {
  const errors = [];
  const out = { days: [] };
  const sedesSet = new Set((validSedes || []).map((s) => String(s)));
  const days = (schedule && schedule.days) || [];
  for (let d = 0; d < DAYS_PER_WEEK; d++) {
    const day = Array.isArray(days[d]) ? days[d] : [];
    const cleaned = [];
    for (const r of day) {
      if (!r) continue;
      const start = typeof r.start === 'number' ? r.start : hhmmToMinutes(r.start);
      const end = typeof r.end === 'number' ? r.end : hhmmToMinutes(r.end);
      if (start == null || end == null) {
        errors.push({ day: d, code: 'INVALID_TIME' });
        continue;
      }
      if (start >= end) {
        errors.push({ day: d, code: 'START_NOT_LESS_THAN_END' });
        continue;
      }
      if (start % TIME_GRANULARITY !== 0 || end % TIME_GRANULARITY !== 0) {
        errors.push({ day: d, code: 'GRANULARITY' });
        continue;
      }
      let sede = r.sede ? String(r.sede).trim() : null;
      if (sede === '') sede = null;
      if (sede && !sedesSet.has(sede)) {
        errors.push({ day: d, code: 'UNKNOWN_SEDE', sede });
        continue;
      }
      cleaned.push({ start, end, sede });
    }
    cleaned.sort((a, b) => a.start - b.start);
    if (cleaned.length > MAX_RANGES_PER_DAY) {
      errors.push({ day: d, code: 'TOO_MANY_RANGES' });
      cleaned.length = MAX_RANGES_PER_DAY;
    }
    out.days.push(cleaned);
  }
  return { schedule: out, errors };
}

/**
 * Cuenta rangos no vacíos en el schedule. Sirve para saber si "está vacío"
 * (el server borra la fila + flag si totalRanges === 0).
 */
export function totalRanges(schedule) {
  if (!schedule || !schedule.days) return 0;
  let n = 0;
  for (const day of schedule.days) n += (day || []).length;
  return n;
}

/**
 * Sumario textual compacto del schedule para mostrar en cards/listas:
 *   "Lun 08:30–12:00 en Centro · Mar 14:00–18:00 · Sáb 10:00–13:00"
 *
 * `dayLabels` debe ser un array de 7 strings (medium). Si una sede ya no
 * está en `validSedes`, se renderiza con prefix "(eliminada) ".
 */
export function summarizeSchedule(schedule, dayLabels = DAY_LABELS_MEDIUM_ES, validSedes = []) {
  if (!schedule || !schedule.days) return '';
  const sedesSet = new Set((validSedes || []).map((s) => String(s)));
  const out = [];
  for (let d = 0; d < DAYS_PER_WEEK; d++) {
    const day = schedule.days[d] || [];
    for (const r of day) {
      const range = `${minutesToHHMM(r.start)}–${minutesToHHMM(r.end)}`;
      let suffix = '';
      if (r.sede) {
        const orphan = !sedesSet.has(r.sede);
        suffix = orphan ? ` en ${r.sede} (eliminada)` : ` en ${r.sede}`;
      }
      out.push(`${dayLabels[d]} ${range}${suffix}`);
    }
  }
  return out.join(' · ');
}

/**
 * Validación de sedes (lado cliente, defensivo). El servidor también valida.
 * Devuelve { sedes, errors[] }.
 */
export function validateSedes(input) {
  const errors = [];
  const seen = new Set();
  const out = [];
  for (const raw of (input || [])) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    if (trimmed.length > MAX_SEDE_LENGTH) {
      errors.push({ code: 'TOO_LONG', value: trimmed });
      continue;
    }
    if (trimmed.indexOf('\n') >= 0 || trimmed.indexOf('\t') >= 0) {
      errors.push({ code: 'BAD_CHAR', value: trimmed });
      continue;
    }
    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(trimmed);
    if (out.length > MAX_SEDES) {
      errors.push({ code: 'TOO_MANY' });
      break;
    }
  }
  return { sedes: out.slice(0, MAX_SEDES), errors };
}
